/**
 * useCache Hook - Simplifies caching for React components
 * CACHING IS DISABLED - skipCache is forced to true globally
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { cacheManager } from './cacheManager';

const CACHING_ENABLED = false; // GLOBAL CACHE DISABLE SWITCH

/**
 * Safe wrapper for partner data - handles null/undefined responses and retries
 * @returns {Object} { data, loading, error }
 */
export function useSafePartnerData() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    
    // Prevent infinite retries
    if (retryCount >= 2) {
      setError('Unable to load partners after multiple attempts');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/partners`,
          { signal: AbortSignal.timeout(30000) }
        );
        
        if (!response.ok) throw new Error('Network error');
        
        const text = await response.text();
        if (text.length > 10000000) throw new Error('Response too large');
        
        const json = JSON.parse(text);
        const partners = Array.isArray(json?.data) ? json.data : [];
        
        if (mounted) {
          setData(partners);
          setError(null);
        }
      } catch (err) {
        console.error('Partner fetch error:', err);
        if (mounted) {
          setError(err.message);
          setRetryCount(prev => prev + 1);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [retryCount]);

  return { data, loading, error };
}

/**
 * Hook for fetching data with automatic caching and stale-while-revalidate
 * @param {string} endpoint - The API endpoint
 * @param {Function} fetchFn - The async function to fetch data
 * @param {number|Object} ttlOrOptions - TTL in ms or options object
 * @returns {Object} { data, loading, error, isStale, refetch, clearCache }
 */
export const useCachedData = (endpoint, fetchFn, ttlOrOptions = {}) => {
  // Handle both old signature (ttl as number) and new signature (options object)
  const options = typeof ttlOrOptions === 'number' 
    ? { ttl: ttlOrOptions }
    : ttlOrOptions;

  const {
    skipCache = !CACHING_ENABLED, // Force skip cache if caching disabled
    onError = null,
    onSuccess = null,
    retryCount = 3,
    retryDelay = 1000,
    useStaleWhileRevalidate = false, // Disable SWR when caching is disabled
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const isMountedRef = useRef(true);
  // Keep a stable ref to the fetch function so callers can pass inline functions
  // without causing fetchData to be re-created on every render.
  const fetchFnRef = useRef(fetchFn);
  useEffect(() => { fetchFnRef.current = fetchFn; }, [fetchFn]);

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      // Check cache first
      if (!forceRefresh && !skipCache) {
        const cached = cacheManager.get(endpoint);
        if (cached) {
          const meta = cacheManager.getMeta(endpoint);
          console.log(`[Hook] Using cached data for ${endpoint}`, { meta });
          if (isMountedRef.current) {
            setData(cached);
            setLoading(false);
            setError(null);
            setIsStale(false);
          }
          onSuccess?.(cached);
          return;
        }
      }

      // If Stale-While-Revalidate is enabled and we have stale cache, use it immediately
      if (useStaleWhileRevalidate && !forceRefresh && !skipCache) {
        const staleCache = cacheManager.get(endpoint, { allowStale: true });
        if (staleCache) {
          const meta = cacheManager.getMeta(endpoint);
          console.log(`[Hook] Serving stale cache for ${endpoint}, fetching fresh in background...`, { meta });
          if (isMountedRef.current) {
            setData(staleCache);
            setLoading(false); // Data is available, not loading anymore
            setError(null);
            setIsStale(true);
          }
          onSuccess?.(staleCache);
          // Continue to fetch fresh data in background
        } else {
          // No cache at all, show loading state
          if (isMountedRef.current) {
            setLoading(true);
            setError(null);
          }
        }
      } else {
        // Traditional behavior: show loading until data arrives
        if (isMountedRef.current) {
          setLoading(true);
          setError(null);
        }
      }

      let lastError = null;
      for (let attempt = 0; attempt <= retryCount; attempt++) {
        try {
          const result = await fetchFnRef.current();
          const responseData = result.data || result;

          if (!isMountedRef.current) return;

          // Cache the data
          cacheManager.set(endpoint, responseData);
          if (isMountedRef.current) {
            setData(responseData);
            setLoading(false);
            setError(null);
            setIsStale(false);
          }
          onSuccess?.(responseData);
          console.log(`[Hook] Fresh data fetched for ${endpoint}`);
          return;
        } catch (err) {
          lastError = err;
          
          // Try stale cache on error
          if (!forceRefresh && !skipCache) {
            const staleCache = cacheManager.get(endpoint, { allowStale: true });
            if (staleCache && isMountedRef.current) {
              console.warn(`[Hook] Fetch failed, serving stale cache for ${endpoint}`);
              setData(staleCache);
              setLoading(false);
              setError(null);
              setIsStale(true);
              onSuccess?.(staleCache);
              return;
            }
          }

          // Retry logic
          if (attempt < retryCount) {
            console.warn(
              `[Hook] Fetch failed (attempt ${attempt + 1}/${retryCount}), retrying...`,
              err.message
            );
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }
      }

      // All retries failed
      if (isMountedRef.current) {
        setData(null);
        setLoading(false);
        setError(lastError);
        setIsStale(false);
      }
      onError?.(lastError);
    },
    // Note: fetchFn is intentionally NOT included in the dependency array; it's
    // referenced via fetchFnRef to avoid re-creating fetchData when callers
    // provide inline functions. Endpoint and options control refetching.
    [endpoint, skipCache, onError, onSuccess, retryCount, retryDelay, useStaleWhileRevalidate]
  );

  useEffect(() => {
    isMountedRef.current = true;
    fetchData();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  const clearCache = useCallback(() => {
    cacheManager.clear(endpoint);
  }, [endpoint]);

  return {
    data,
    loading,
    error,
    isStale,
    refetch,
    clearCache,
  };
};

/**
 * Hook for cache statistics
 * @returns {Object} Cache statistics
 */
export const useCacheStats = () => {
  const [stats, setStats] = useState(cacheManager.getStats());

  const refreshStats = useCallback(() => {
    setStats(cacheManager.getStats());
  }, []);

  const clearStats = useCallback(() => {
    cacheManager.clearStats();
    refreshStats();
  }, [refreshStats]);

  return {
    stats,
    refreshStats,
    clearStats,
  };
};

/**
 * Hook for cache invalidation patterns
 * Useful for clearing related caches after mutations
 */
export const useInvalidateCache = () => {
  return {
    // Clear specific endpoint
    clear: (endpoint) => {
      cacheManager.clear(endpoint);
    },

    // Clear all caches
    clearAll: () => {
      cacheManager.clearAll();
    },

    // Clear by pattern (regex)
    clearPattern: (pattern) => {
      cacheManager.invalidatePattern(pattern);
    },

    // Clear collection cache
    clearCollection: (collection) => {
      cacheManager.invalidatePattern(`/api/${collection}`);
    },

    // Clear specific resource and its collection
    clearResource: (collection, id) => {
      cacheManager.clear(`/api/${collection}/${id}`);
      cacheManager.invalidatePattern(`/api/${collection}`);
    },
  };
};

/**
 * Hook for listening to cache invalidation events
 * Automatically refetches data when specified endpoints are cleared from cache
 * @param {string|string[]} endpoints - Endpoint(s) to listen for (e.g., '/api/partners')
 * @param {Function} refetchFn - Function to call when cache is cleared
 * @returns {void}
 * @example
 * useCacheInvalidationListener('/api/partners', refetch);
 * useCacheInvalidationListener(['/api/partners', '/api/advisors'], refetch);
 */
export const useCacheInvalidationListener = (endpoints, refetchFn) => {
  useEffect(() => {
    if (!refetchFn || !endpoints) return;

    // Normalize endpoints to array
    const endpointList = Array.isArray(endpoints) ? endpoints : [endpoints];

    // Create subscription callback
    const handleCacheInvalidation = (invalidatedEndpoint) => {
      // Check if this invalidation affects any of our watched endpoints
      const shouldRefetch = 
        invalidatedEndpoint === '*' || // All caches cleared
        endpointList.includes(invalidatedEndpoint) || // Exact match
        endpointList.some(ep => invalidatedEndpoint.startsWith(ep)); // Pattern match

      if (shouldRefetch) {
        console.log(
          `[useCacheInvalidationListener] Cache cleared for ${invalidatedEndpoint}, refetching`
        );
        refetchFn();
      }
    };

    // Subscribe to cache invalidation events
    const unsubscribe = cacheManager.subscribe(handleCacheInvalidation);

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [endpoints, refetchFn]);
};

export default useCachedData;
